import { addDays } from './dates';
import { syncTracking } from './presence';
import { type RecurrenceRule, isSkipped, legacyWeeklyRule, patternDates } from './recurrence';
import { servedByRole } from './roleRules';
import type { ScheduleEvent } from './types';

/**
 * Histórico real das escalas: criar as ocorrências passadas de um evento recorrente
 * (Cadastros › Eventos › "Ocorrências passadas") e registrar quem serviu em cada uma
 * (Escalas › Histórico). As datas registradas entram como histórico real em tudo o que
 * já usa a escala dos eventos passados: os indicadores do membro, a Distribuição e o
 * equilíbrio da geração automática.
 */

/** Quantas datas, no máximo, uma criação de ocorrências passadas cria de uma vez. */
export const BACKFILL_LIMIT = 160;

export interface BackfillPlan {
  /**
   * O modelo das novas: a configuração atual da série (a última data não editada
   * sozinha), para quem editou a série antes ver a edição nas datas criadas.
   */
  template: ScheduleEvent | null;
  /** A primeira data já existente: as novas vão até a véspera dela. */
  first: string | null;
  /** As datas que serão criadas, da mais antiga para a mais recente. */
  dates: string[];
  /** Havia mais datas que o limite: repita a operação para ir mais longe. */
  truncated: boolean;
}

function seriesOf(events: readonly ScheduleEvent[], seriesId: string): ScheduleEvent[] {
  return events
    .filter((e) => e.seriesId === seriesId)
    .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
}

/**
 * A regra da série: a guardada nas datas ou, na série antiga que só tem o rótulo
 * ("Semanal · …"), a semanal no dia da primeira data. `null` no evento único.
 */
export function seriesRule(events: readonly ScheduleEvent[], seriesId: string): RecurrenceRule | null {
  const series = seriesOf(events, seriesId);
  const withRule = series.find((e) => e.rule);

  if (withRule) return withRule.rule;

  return series[0]?.recurrence ? legacyWeeklyRule(series[0].date) : null;
}

/** Só séries que se repetem (semanal, mensal, anual…) recebem ocorrências passadas. */
export function canBackfill(events: readonly ScheduleEvent[], seriesId: string): boolean {
  return !!seriesRule(events, seriesId);
}

/**
 * As datas da regra da série desde `since` até a véspera da primeira data já existente,
 * sem repetir nenhuma — o mesmo padrão das datas criadas (cada domingo, o terceiro domingo
 * do mês, quinzenal…). Passando do limite, ficam as mais recentes — repetir a operação
 * continua de onde parou.
 */
export function backfillPlan(
  events: readonly ScheduleEvent[],
  seriesId: string,
  since: string
): BackfillPlan {
  const series = seriesOf(events, seriesId);
  const first = series[0]?.date ?? null;
  const template = series.filter((e) => !e.edited).at(-1) ?? series.at(-1) ?? null;

  const rule = seriesRule(events, seriesId);

  if (!rule || !first || !since || since >= first) {
    return { template, first, dates: [], truncated: false };
  }

  const existing = new Set(series.map((e) => e.date));
  // As datas excluídas sozinhas ("Só esta data") não voltam.
  const dates = patternDates(rule, since, addDays(first, -1)).filter(
    (d) => !existing.has(d) && !isSkipped(rule, d)
  );

  return {
    template,
    first,
    dates: dates.slice(-BACKFILL_LIMIT),
    truncated: dates.length > BACKFILL_LIMIT,
  };
}

/** Cria as ocorrências passadas, com os mesmos ministérios e funções da série. */
export function backfillEvents(
  events: ScheduleEvent[],
  seriesId: string,
  since: string
): { events: ScheduleEvent[]; created: ScheduleEvent[] } {
  const { template, dates } = backfillPlan(events, seriesId, since);
  const rule = seriesRule(events, seriesId);

  if (!template || !dates.length) return { events, created: [] };

  const ids = new Set(events.map((e) => e.id));
  const created = dates.map((date): ScheduleEvent => {
    const id = ids.has(`${seriesId}-${date}`) ? `${seriesId}-${date}-p` : `${seriesId}-${date}`;

    return {
      id,
      seriesId,
      title: template.title,
      date,
      time: template.time,
      recurrence: template.recurrence,
      rule,
      color: template.color,
      ministryIds: [...template.ministryIds],
      need: { ...template.need },
      slots: {},
      generated: false,
      published: false,
      edited: false,
      responses: {},
      attendance: {},
      nudgedAt: null,
      recorded: false,
      notices: {},
      swaps: [],
      audience: null,
    };
  });

  return {
    events: [...events, ...created].sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time)),
    created,
  };
}

/** Quantas ocorrências da série já aconteceram ("N passadas" no card do evento). */
export function pastOccurrences(events: readonly ScheduleEvent[], seriesId: string, today: string): number {
  return events.filter((e) => e.seriesId === seriesId && e.date < today).length;
}

/** Situação do registro: não registrado, em andamento (alguém marcado) ou registrado. */
export type HistoryState = 'empty' | 'partial' | 'recorded';

export const HISTORY_STATE_LABEL: Record<HistoryState, string> = {
  empty: 'Não registrado',
  partial: 'Em andamento',
  recorded: 'Registrado',
};

export function historyStateOf(event: ScheduleEvent): HistoryState {
  if (event.recorded) return 'recorded';

  return Object.values(event.slots).some((ids) => ids.length) ? 'partial' : 'empty';
}

/** Os eventos que já aconteceram (dos ministérios do recorte), do mais recente ao mais antigo. */
export function historyEvents(
  events: readonly ScheduleEvent[],
  today: string,
  ministryIds: readonly string[] | null = null
): ScheduleEvent[] {
  return events
    .filter((e) => e.date < today && (!ministryIds || e.ministryIds.some((id) => ministryIds.includes(id))))
    .sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
}

/** A data anterior da mesma série (a do "Copiar do evento anterior"). */
export function previousInSeries(
  events: readonly ScheduleEvent[],
  event: ScheduleEvent
): ScheduleEvent | null {
  return (
    seriesOf(events, event.seriesId)
      .filter((e) => (e.date + e.time).localeCompare(event.date + event.time) < 0)
      .at(-1) ?? null
  );
}

/** Quantas vezes cada pessoa serviu na função ao longo da série (fora o próprio evento). */
export function seriesFrequency(
  events: readonly ScheduleEvent[],
  event: ScheduleEvent,
  roleId: string
): Map<string, number> {
  const counts = new Map<string, number>();

  for (const e of events) {
    if (e.seriesId !== event.seriesId || e.id === event.id) continue;

    for (const id of servedByRole(e)[roleId] ?? []) counts.set(id, (counts.get(id) ?? 0) + 1);
  }

  return counts;
}

/** Registro mexe na escala do evento: tem escala (gerado) e as marcações acompanham. */
function withSlots(event: ScheduleEvent, slots: Record<string, string[]>, attendance = event.attendance) {
  const generated = event.generated || Object.values(slots).some((ids) => ids.length > 0);

  return syncTracking({ ...event, slots, attendance, generated });
}

/**
 * Marca ou desmarca quem serviu na função. Marcar quem faltou no check-in desfaz a falta;
 * desmarcar quem serviu no lugar de alguém tira só a substituição.
 */
export function setServed(
  event: ScheduleEvent,
  roleId: string,
  memberId: string,
  served: boolean
): ScheduleEvent {
  const ids = event.slots[roleId] ?? [];
  const attendance = { ...event.attendance };

  if (served) {
    if ((servedByRole(event)[roleId] ?? []).includes(memberId)) return event;
    if (ids.includes(memberId)) {
      delete attendance[memberId];

      return withSlots(event, event.slots, attendance);
    }

    return withSlots(event, { ...event.slots, [roleId]: [...ids, memberId] }, attendance);
  }

  if (ids.includes(memberId)) {
    delete attendance[memberId];

    return withSlots(event, { ...event.slots, [roleId]: ids.filter((id) => id !== memberId) }, attendance);
  }

  const covered = ids.find((id) => event.attendance[id]?.substituteId === memberId);

  if (!covered) return event;

  attendance[covered] = { status: 'absent', substituteId: null };

  return withSlots(event, event.slots, attendance);
}

/** Troca quem serviu nas funções por `served` (as demais ficam como estão). */
function replaceServed(event: ScheduleEvent, served: Record<string, string[]>): ScheduleEvent {
  const slots = { ...event.slots };
  const attendance = { ...event.attendance };

  for (const [roleId, ids] of Object.entries(served)) {
    (slots[roleId] ?? []).forEach((id) => delete attendance[id]);
    slots[roleId] = [...ids];
  }

  return withSlots(event, slots, attendance);
}

/** "Copiar do evento anterior": quem serviu na data anterior da série, nas funções dadas. */
export function copyServedFromPrevious(
  events: readonly ScheduleEvent[],
  event: ScheduleEvent,
  roleIds: readonly string[]
): ScheduleEvent | null {
  const previous = previousInSeries(events, event);

  if (!previous) return null;

  const served = servedByRole(previous);

  return replaceServed(event, Object.fromEntries(roleIds.map((id) => [id, served[id] ?? []])));
}

/** "Limpar": tira todas as marcações das funções dadas. */
export function clearServed(event: ScheduleEvent, roleIds: readonly string[]): ScheduleEvent {
  return replaceServed(event, Object.fromEntries(roleIds.map((id) => [id, []])));
}

/** "Concluir" marca a data como registrada; "Reabrir" volta a edição. */
export function setRecorded(event: ScheduleEvent, recorded: boolean): ScheduleEvent {
  return event.recorded === recorded ? event : { ...event, recorded };
}

/** O próximo evento pendente (não registrado) depois do atual, na ordem da lista. */
export function nextPending(list: readonly ScheduleEvent[], currentId: string): ScheduleEvent | null {
  const at = list.findIndex((e) => e.id === currentId);
  const after = [...list.slice(at + 1), ...list.slice(0, Math.max(0, at))];

  return after.find((e) => e.id !== currentId && !e.recorded) ?? null;
}
